import React, { useContext } from 'react';
import { graphql } from 'gatsby';
import * as styles from './form-container-w-image.module.css';

import {
    KontentMedia,
    KontentRadioMultiChoice,
    KontentStringValue,
} from '../../../types/KontentProps';
import {
    getMediaData,
    getMultiChoice,
    optimizeKontentImageUrl,
    imagePreset,
    getDisplayDimensions,
    buildKontentSrcSet,
} from '../Global/DataUtils';
import LazyImage from '../Global/LazyImage';

import Jotform from '../Jotform';
import Tally from '../Tally';
import CustomHeadline from '../Global/CustomHeadline';
import { PageMetadataContext } from '../../templates/PageMetaDataContext';

type HeaderTagType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p';
type DesktopImagePositionOptions = 'left' | 'right';
type MobileImagePositionOptions = 'top' | 'bottom';

interface Props {
    kontentItemId?: string;
    desktop_image: KontentMedia;
    desktop_image_position?: KontentRadioMultiChoice<DesktopImagePositionOptions>;
    mobile_image: KontentMedia;
    mobile_image_position?: KontentRadioMultiChoice<MobileImagePositionOptions>;
    form_headline?: KontentStringValue;
    form_description?: KontentStringValue;
    form_link: KontentStringValue;
    anchor: KontentStringValue;
    control_bottom_margin?: KontentRadioMultiChoice<string>;
    desktop_bottom_margin?: KontentStringValue;
    mobile_bottom_margin?: KontentStringValue;
    headline_style?: KontentRadioMultiChoice<HeaderTagType>;
    headline_tag?: KontentRadioMultiChoice<HeaderTagType>;
}

export const FormContainerWImage: React.FC<Props> = ({
    desktop_image,
    desktop_image_position,
    mobile_image,
    mobile_image_position,
    form_headline,
    form_description,
    form_link,
    anchor,
    control_bottom_margin,
    desktop_bottom_margin,
    mobile_bottom_margin,
    headline_style,
    headline_tag,
    kontentItemId,
}) => {
    const { slug } = useContext(PageMetadataContext);
    const controlMargin = control_bottom_margin?.value?.[0]?.codename === 'yes';
    const DesktopImage = getMediaData(desktop_image);
    const MobileImage = getMediaData(mobile_image);
    const DesktopImgPosition =
        desktop_image_position?.value[0]?.codename == 'right';
    const MobileImgPosition =
        mobile_image_position?.value[0]?.codename == 'bottom';

    const isTallyForm = form_link?.value?.includes('tally.so') || false;

    const containerClasses = [
        styles.container,
        DesktopImgPosition ? styles.imageRight : '',
        MobileImgPosition ? styles.imageDown : '',
        controlMargin ? styles.controlMargin : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div
            className={containerClasses}
            data-kontent-item-id={kontentItemId}
            style={
                controlMargin
                    ? ({
                          '--desktop-margin': `${
                              desktop_bottom_margin?.value || 0
                          }px`,
                          '--mobile-margin': `${
                              mobile_bottom_margin?.value || 0
                          }px`,
                      } as React.CSSProperties)
                    : undefined
            }
        >
            <div className={styles.imageContainer}>
                {(DesktopImage?.url || MobileImage?.url) && (
                    <picture data-kontent-element-codename="desktop_image">
                        {MobileImage?.url && DesktopImage?.url && (
                            <source
                                media="(max-width: 576px)"
                                srcSet={buildKontentSrcSet(
                                    MobileImage.url,
                                    imagePreset.fullWidth.mobile.w,
                                    {
                                        fm: 'webp' as const,
                                        q: imagePreset.fullWidth.mobile.q,
                                    }
                                )}
                                sizes="100vw"
                                {...getDisplayDimensions(
                                    MobileImage.width,
                                    MobileImage.height,
                                    imagePreset.fullWidth.mobile.w
                                )}
                            />
                        )}
                        {DesktopImage?.url && MobileImage?.url && (
                            <source
                                media="(min-width: 577px) and (max-width: 991px)"
                                srcSet={buildKontentSrcSet(
                                    DesktopImage.url,
                                    imagePreset.fullWidth.tablet.w,
                                    {
                                        fm: 'webp' as const,
                                        q: imagePreset.fullWidth.tablet.q,
                                    }
                                )}
                                sizes="100vw"
                                {...getDisplayDimensions(
                                    DesktopImage.width,
                                    DesktopImage.height,
                                    imagePreset.fullWidth.tablet.w
                                )}
                            />
                        )}
                        <LazyImage
                            src={optimizeKontentImageUrl(
                                (DesktopImage?.url || MobileImage?.url)!,
                                DesktopImage?.url
                                    ? imagePreset.fullWidth.desktop
                                    : imagePreset.fullWidth.mobile
                            )}
                            srcSet={buildKontentSrcSet(
                                (DesktopImage?.url || MobileImage?.url)!,
                                DesktopImage?.url
                                    ? imagePreset.fullWidth.desktop.w
                                    : imagePreset.fullWidth.mobile.w,
                                {
                                    fm: 'webp' as const,
                                    q: imagePreset.fullWidth.desktop.q,
                                }
                            )}
                            sizes="(max-width: 991px) 100vw, 50vw"
                            alt={
                                DesktopImage?.description ||
                                DesktopImage?.name ||
                                MobileImage?.description ||
                                MobileImage?.name ||
                                ''
                            }
                            {...(DesktopImage?.url
                                ? getDisplayDimensions(
                                      DesktopImage.width,
                                      DesktopImage.height,
                                      imagePreset.fullWidth.desktop.w
                                  )
                                : getDisplayDimensions(
                                      MobileImage?.width,
                                      MobileImage?.height,
                                      imagePreset.fullWidth.mobile.w
                                  ))}
                        />
                    </picture>
                )}
            </div>

            <div
                id={anchor.value ? anchor.value : ''}
                className={styles.formContainer}
            >
                <CustomHeadline
                    style={getMultiChoice(headline_style) || 'h2'}
                    tag={
                        slug === 'get-pricing'
                            ? 'h1'
                            : getMultiChoice(headline_tag) || 'h2'
                    }
                    headline_value={form_headline}
                    className={styles.headline}
                    elementCodename="form_headline"
                />
                <p data-kontent-element-codename="form_description">
                    {form_description?.value}
                </p>
                <div className={isTallyForm ? styles.tallyForm : ''}>
                    {isTallyForm ? (
                        <Tally formUrl={form_link.value} />
                    ) : (
                        <Jotform
                            formUrl={form_link.value}
                            formStyle="embed"
                            display={true}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
export default FormContainerWImage;

export const fragmentgp = graphql`
    fragment FormContainerWImage on kontent_item_component___form_container_w__image {
        elements {
            desktop_image {
                ...kontentImage
            }
            desktop_image_position {
                value {
                    codename
                }
            }
            mobile_image_position {
                value {
                    codename
                }
            }
            mobile_image {
                ...kontentImage
            }
            form_headline {
                value
            }
            form_description {
                value
            }
            form_link {
                value
            }
            anchor {
                value
            }
            control_bottom_margin {
                value {
                    name
                    codename
                }
            }
            mobile_bottom_margin {
                value
            }
            desktop_bottom_margin {
                value
            }
            headline_tag {
                value {
                    name
                    codename
                }
            }
            headline_style {
                value {
                    name
                    codename
                }
            }
        }
    }
`;
